import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Shield, Gift, TrendingUp, Users, ArrowRight, Eye, EyeOff, Check } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MILESTONES = [
  { threshold: 5, reward: "€15 Bol.com", color: "#10B981" },
  { threshold: 10, reward: "€35 Bol.com", color: "#3B82F6" },
  { threshold: 25, reward: "€100 Bol.com", color: "#8B5CF6" },
  { threshold: 50, reward: "€250 bonus", color: "#F59E0B" },
  { threshold: 100, reward: "€500 bonus", color: "#EF4444" },
];

export default function PartnerSignup() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    address: "",
    postal_code: "",
    city: "",
    country: "Nederland",
    terms_accepted: false,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.terms_accepted) {
      toast.error("Je moet akkoord gaan met de voorwaarden");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/affiliate/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Registratie mislukt");
      }

      // Store token in localStorage
      localStorage.setItem("partner_token", data.token);
      localStorage.setItem("partner_data", JSON.stringify({
        affiliate_id: data.affiliate_id,
        referral_code: data.referral_code,
        name: `${formData.first_name} ${formData.last_name}`,
      }));

      toast.success("Registratie succesvol! Je wordt doorgestuurd...");
      
      setTimeout(() => {
        navigate("/partner-dashboard");
      }, 1500);

    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800/50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-white">Proofix</span>
          </Link>
          <Link to="/partner-login">
            <Button variant="ghost" className="text-slate-400 hover:text-white">
              Inloggen
            </Button>
          </Link>
        </div>
      </header>

      <div className="pt-24 pb-16">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-start max-w-6xl mx-auto">
            
            {/* Left Column - Benefits */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:sticky lg:top-32"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full mb-6">
                <Gift className="w-4 h-4 text-blue-400" />
                <span className="text-sm text-blue-400 font-medium">Proofix Partner Programma</span>
              </div>

              <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Verdien met Proofix
              </h1>

              <p className="text-lg text-slate-400 mb-8">
                Help ondernemers inzicht te krijgen en ontvang beloningen voor elke betaalde referral.
              </p>

              {/* Benefits List */}
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4 p-4 bg-slate-900/50 rounded-xl border border-slate-800">
                  <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Gratis aanmelden</h3>
                    <p className="text-sm text-slate-400">Geen kosten, geen verplichtingen. Direct starten.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-slate-900/50 rounded-xl border border-slate-800">
                  <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Unieke referral link</h3>
                    <p className="text-sm text-slate-400">Krijg je eigen link om te delen met je netwerk.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-slate-900/50 rounded-xl border border-slate-800">
                  <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Gift className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Milestone beloningen</h3>
                    <p className="text-sm text-slate-400">Verdien steeds meer naarmate je groeit.</p>
                  </div>
                </div>
              </div>

              {/* Milestones Preview */}
              <div className="bg-slate-900/30 rounded-xl border border-slate-800 p-5">
                <h3 className="text-sm font-medium text-slate-400 mb-4">Beloningen overzicht</h3>
                <div className="space-y-3">
                  {MILESTONES.map((milestone) => (
                    <div key={milestone.threshold} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold text-white"
                          style={{ backgroundColor: `${milestone.color}20`, color: milestone.color }}
                        >
                          {milestone.threshold}
                        </div>
                        <span className="text-slate-300 text-sm">betaalde referrals</span>
                      </div>
                      <span className="font-semibold text-white">{milestone.reward}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Right Column - Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="bg-slate-900/50 rounded-2xl border border-slate-800 p-6 sm:p-8">
                <h2 className="text-xl font-semibold text-white mb-6">Maak je account aan</h2>

                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Name Row */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="first_name" className="text-slate-300">Voornaam *</Label>
                      <Input
                        id="first_name"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        required
                        className="bg-slate-950/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-blue-500"
                        placeholder="Jan"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name" className="text-slate-300">Achternaam *</Label>
                      <Input
                        id="last_name"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        required
                        className="bg-slate-950/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-blue-500"
                        placeholder="Jansen"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-300">E-mailadres *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="bg-slate-950/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-blue-500"
                      placeholder="jan@voorbeeld.nl"
                    />
                  </div>

                  {/* Password */}
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-slate-300">Wachtwoord *</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={handleChange}
                        required
                        minLength={6}
                        className="bg-slate-950/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-blue-500 pr-10"
                        placeholder="Minimaal 6 tekens"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Address */}
                  <div className="space-y-2">
                    <Label htmlFor="address" className="text-slate-300">Adres *</Label>
                    <Input
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      className="bg-slate-950/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-blue-500"
                      placeholder="Straatnaam 123"
                    />
                  </div>

                  {/* Postal & City Row */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="postal_code" className="text-slate-300">Postcode *</Label>
                      <Input
                        id="postal_code"
                        name="postal_code"
                        value={formData.postal_code}
                        onChange={handleChange}
                        required
                        className="bg-slate-950/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-blue-500"
                        placeholder="1234 AB"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="city" className="text-slate-300">Woonplaats *</Label>
                      <Input
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        required
                        className="bg-slate-950/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-blue-500"
                        placeholder="Amsterdam"
                      />
                    </div>
                  </div>

                  {/* Country */}
                  <div className="space-y-2">
                    <Label htmlFor="country" className="text-slate-300">Land *</Label>
                    <Input
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      required
                      className="bg-slate-950/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-blue-500"
                      placeholder="Nederland"
                    />
                  </div>

                  {/* Terms */}
                  <div className="flex items-start gap-3 pt-2">
                    <Checkbox
                      id="terms"
                      checked={formData.terms_accepted}
                      onCheckedChange={(checked) => 
                        setFormData((prev) => ({ ...prev, terms_accepted: checked }))
                      }
                      className="mt-1 border-slate-600 data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500"
                    />
                    <Label htmlFor="terms" className="text-sm text-slate-400 cursor-pointer leading-relaxed">
                      Ik ga akkoord met de voorwaarden van het Proofix Partner Programma en het privacybeleid.
                    </Label>
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold py-6 rounded-xl"
                    data-testid="partner-signup-submit"
                  >
                    {isLoading ? (
                      <span className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Registreren...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        Start als partner
                        <ArrowRight className="w-5 h-5" />
                      </span>
                    )}
                  </Button>

                  <p className="text-center text-sm text-slate-500">
                    Al partner?{" "}
                    <Link to="/partner-login" className="text-blue-400 hover:text-blue-300">
                      Log hier in
                    </Link>
                  </p>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
